/**
 * ScoreHistoryChart — Strength score over time (bar chart).
 *
 * Pro users see the full chart with trend line and milestones.
 * Free users see a blurred teaser with upgrade prompt.
 *
 * Reads from strength_history table (weekly snapshots).
 * Zero effect on scoring/matchmaking — read-only display.
 */

import { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/lib/supabase';
import { useRouter } from 'expo-router';
import { TYPO } from '@/constants/typography';
import { TrendingUp, Lock } from 'lucide-react-native';

interface DataPoint {
  date: string;
  score: number;
}

interface MilestonePoint {
  index: number;
  label: string;
}

export default function ScoreHistoryChart() {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const { isProPlus } = useSubscription();
  const router = useRouter();
  const c = theme.colors;

  const [points, setPoints] = useState<DataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [milestones, setMilestones] = useState<MilestonePoint[]>([]);

  useEffect(() => {
    if (!profile?.id) return;
    loadHistory();
  }, [profile?.id]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      // Pro users get more history; free get 12 weeks
      const limit = isProPlus ? 52 : 12;

      const { data, error } = await supabase
        .from('strength_history')
        .select('snapshot_date, score')
        .eq('profile_id', profile!.id)
        .order('snapshot_date', { ascending: true })
        .limit(limit);

      if (!error && data && data.length > 0) {
        const pts = data.map((d: any) => ({
          date: d.snapshot_date,
          score: d.score ?? 0,
        }));
        setPoints(pts);

        // Find milestones: points where score jumped >10% from previous
        const ms: MilestonePoint[] = [];
        for (let i = 1; i < pts.length; i++) {
          const prev = pts[i - 1].score;
          const curr = pts[i].score;
          if (prev > 0 && curr > 0 && (curr - prev) / prev >= 0.10) {
            ms.push({ index: i, label: `+${Math.round(((curr - prev) / prev) * 100)}%` });
          }
        }
        setMilestones(ms);
      } else {
        // Fallback: use workouts to approximate weekly scores
        const { data: workouts } = await supabase
          .from('workouts')
          .select('performed_at, total_volume')
          .eq('user_id', profile!.id)
          .order('performed_at', { ascending: true })
          .limit(60);

        if (workouts && workouts.length > 0) {
          const weekMap = new Map<string, number>();
          for (const w of workouts as any[]) {
            const d = new Date(w.performed_at);
            const weekKey = getWeekKey(d);
            weekMap.set(weekKey, (weekMap.get(weekKey) ?? 0) + (w.total_volume ?? 0));
          }
          const weekPts = Array.from(weekMap.entries())
            .slice(isProPlus ? -52 : -12)
            .map(([date, vol]) => ({ date, score: Math.round(vol / 100) }));
          setPoints(weekPts);
        } else {
          setPoints([]);
        }
      }
    } catch (err) {
      console.warn('[ScoreHistoryChart] load error', err);
      setPoints([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <ActivityIndicator color={c.accent} style={{ marginVertical: 16 }} />;
  }

  // No data at all — don't show anything
  if (points.length < 2) return null;

  // ── Free user teaser ──
  if (!isProPlus) {
    return (
      <TouchableOpacity
        style={[styles.container, { backgroundColor: c.surface, borderColor: c.border }]}
        onPress={() => router.push('/paywall')}
        activeOpacity={0.7}
      >
        <View style={styles.headerRow}>
          <TrendingUp size={16} color={c.accent} />
          <Text style={[styles.title, { color: c.text }]}>PROGRESS TREND</Text>
          <Lock size={14} color={c.textTertiary} />
        </View>

        {/* Blurred bars teaser */}
        <View style={styles.chartArea}>
          {points.slice(-12).map((pt, i) => {
            const minScore = Math.min(...points.map((p) => p.score));
            const maxScore = Math.max(...points.map((p) => p.score));
            const range = maxScore - minScore || 1;
            const height = 8 + ((pt.score - minScore) / range) * 40;
            return (
              <View key={`${pt.date}-${i}`} style={styles.barCol}>
                <View
                  style={[
                    styles.bar,
                    { height, backgroundColor: c.accent + '25', borderRadius: 2 },
                  ]}
                />
              </View>
            );
          })}
        </View>

        {/* Overlay text */}
        <View style={styles.teaserOverlay}>
          <Text style={[styles.teaserText, { color: c.accent }]}>
            Upgrade to Pro to see your progress trend
          </Text>
          <Text style={[styles.teaserSubtext, { color: c.textTertiary }]}>
            $14.99/mo · 7-day free trial
          </Text>
        </View>
      </TouchableOpacity>
    );
  }

  // ── Pro full chart ──
  const minScore = Math.min(...points.map((p) => p.score));
  const maxScore = Math.max(...points.map((p) => p.score));
  const range = maxScore - minScore || 1;

  const firstScore = points[0].score;
  const lastScore = points[points.length - 1].score;
  const delta = lastScore - firstScore;
  const trendColor = delta >= 0 ? '#22c55e' : '#ef4444';
  const trendPct = firstScore > 0 ? Math.round((delta / firstScore) * 100) : 0;

  return (
    <View style={[styles.container, { backgroundColor: c.surface, borderColor: c.border }]}>
      <View style={styles.headerRow}>
        <TrendingUp size={16} color={c.accent} />
        <Text style={[styles.title, { color: c.text }]}>PROGRESS TREND</Text>
        <Text style={[styles.delta, { color: trendColor }]}>
          {delta >= 0 ? '+' : ''}{delta} ({trendPct >= 0 ? '+' : ''}{trendPct}%)
        </Text>
      </View>

      {/* Bar chart */}
      <View style={styles.chartArea}>
        {points.map((pt, i) => {
          const height = 8 + ((pt.score - minScore) / range) * 52;
          const isMilestone = milestones.some((m) => m.index === i);
          return (
            <View key={`${pt.date}-${i}`} style={styles.barCol}>
              {isMilestone && (
                <View style={[styles.milestoneDot, { backgroundColor: '#f59e0b' }]} />
              )}
              <View
                style={[
                  styles.bar,
                  {
                    height,
                    backgroundColor:
                      i === points.length - 1
                        ? c.accent
                        : isMilestone
                        ? '#f59e0b99'
                        : c.accent + '55',
                    borderRadius: 2,
                  },
                ]}
              />
            </View>
          );
        })}
      </View>

      {/* Trend line (simplified: just start → end text) */}
      <View style={styles.xAxis}>
        <Text style={[styles.xLabel, { color: c.textTertiary }]}>
          {formatShortDate(points[0].date)}
        </Text>
        {milestones.length > 0 && (
          <Text style={[styles.milestoneLabel, { color: '#f59e0b' }]}>
            ● {milestones.length} milestone{milestones.length > 1 ? 's' : ''}
          </Text>
        )}
        <Text style={[styles.xLabel, { color: c.textTertiary }]}>
          {formatShortDate(points[points.length - 1].date)}
        </Text>
      </View>

      {/* Score summary row */}
      <View style={styles.summaryRow}>
        <View style={styles.summaryItem}>
          <Text style={[styles.summaryValue, { color: c.text }]}>{firstScore}</Text>
          <Text style={[styles.summaryLabel, { color: c.textTertiary }]}>Start</Text>
        </View>
        <View style={styles.summaryItem}>
          <Text style={[styles.summaryValue, { color: c.text }]}>{lastScore}</Text>
          <Text style={[styles.summaryLabel, { color: c.textTertiary }]}>Current</Text>
        </View>
        <View style={styles.summaryItem}>
          <Text style={[styles.summaryValue, { color: trendColor }]}>
            {delta >= 0 ? '+' : ''}{delta}
          </Text>
          <Text style={[styles.summaryLabel, { color: c.textTertiary }]}>Change</Text>
        </View>
      </View>
    </View>
  );
}

function getWeekKey(d: Date): string {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.getFullYear(), d.getMonth(), diff);
  return `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  title: {
    flex: 1,
    fontSize: 13,
    fontFamily: TYPO.subheading.bold,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  delta: {
    fontSize: 14,
    fontFamily: TYPO.heading.bold,
  },
  chartArea: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    height: 64,
    gap: 2,
  },
  barCol: {
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  bar: {
    width: '80%',
    minHeight: 4,
  },
  milestoneDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginBottom: 2,
  },
  xAxis: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 6,
  },
  xLabel: {
    fontSize: 11,
    fontWeight: '600',
  },
  milestoneLabel: {
    fontSize: 11,
    fontWeight: '600',
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: 'rgba(128,128,128,0.15)',
  },
  summaryItem: {
    alignItems: 'center',
    gap: 2,
  },
  summaryValue: {
    fontSize: 16,
    fontFamily: TYPO.heading.bold,
  },
  summaryLabel: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  teaserOverlay: {
    alignItems: 'center',
    marginTop: 12,
    gap: 4,
  },
  teaserText: {
    fontSize: 14,
    fontFamily: TYPO.body.semibold,
    textAlign: 'center',
  },
  teaserSubtext: {
    fontSize: 12,
    fontFamily: TYPO.body.regular,
    textAlign: 'center',
  },
});
